import { SKILL_TYPES, type Skill, type SkillType } from '@clockwise/clients/skills';
import { RATING_STATUSES, type RatingStatus, type User } from '@clockwise/clients/users';
import { type FilterRule, reportSpecSchema, resolveDateRange } from '@clockwise/semantic-core';
import { ApiError } from '@clockwise/shared';
import { Picker } from '@clockwise/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Download, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { EntityMultiSelect } from '@/components/custom/entity-multi-select';
import { skillPickerConfig, userPickerConfig } from '@/components/custom/entity-pickers';
import { MultiSelect } from '@/components/custom/multi-select';
import { NoAccess } from '@/components/custom/no-access';
import { PROFICIENCY_LEVEL_LABELS } from '@/components/custom/proficiency-scale-reference';
import { UserRow } from '@/components/custom/user-row';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { SkillMatrixGridView } from '@/components/reports/skill-matrix-grid';
import { buildSkillMatrixGrid } from '@/components/reports/skill-matrix-grid-data';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { analyticsApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { useMe } from '@/lib/me';
import { queryKeys } from '@/lib/query-keys';

/**
 * Skill matrix — `/reports/skill-matrix`.
 *
 * Employees as columns; skills (proficiency) and this month's hours as rows.
 * No generic Report Builder spec can produce this shape — it mixes two
 * subject areas (`skillMatrix`, `utilization`) and transposes people onto
 * the column axis — so this page runs both as plain analytics queries and
 * merges the flat results itself (`buildSkillMatrixGrid`). Both queries go
 * through the same `AnalyticsService.run` every other report uses, so row
 * visibility (`reportReach`) is identical to any other report's, for free.
 *
 * Two screens, same shape as the Timesheet report (`reports.timesheets.tsx`):
 * pick criteria, then run. Chosen filters are bound into BOTH specs' own
 * `filters` — a real narrowing of what the server returns, not a client-side
 * search over everything already fetched — so picking a handful of people
 * out of a large tenant is cheap rather than "fetch everyone, then hide most
 * of them".
 *
 * `skills.view` ON TOP of the layout's `report.view`, same reasoning as
 * `reports.new.tsx`'s `report.edit` check: the `skillMatrix` model is gated
 * on `skills.view` server-side (`MODEL_PERMISSION`, `core/analytics/model.ts`),
 * so gating here too turns that refusal into an explained page rather than a
 * broken one.
 */
const idList = z.array(z.string().uuid()).optional();
const search = z.object({
  employeeIds: idList,
  skillIds: idList,
  // Raw `users.location` values, not ids — there is no Location catalog, just
  // whatever distinct strings the column holds (see `location` dimension on
  // both models).
  locations: z.array(z.string()).optional(),
  // A THRESHOLD, not a member pick: `proficiencyLevel`'s filter type is
  // `number` (`core/analytics/model.ts`), whose compiler only accepts the
  // ordered operators (gt/gte/lt/lte/between) — "is any of" is refused
  // there (`cannot be filtered with "is any of"`). So this is one value,
  // bound as `gte`, not a multi-select.
  minProficiencyLevel: z.enum(['1', '2', '3', '4', '5']).optional(),
  ratingStatuses: z.array(z.enum(RATING_STATUSES)).optional(),
  // Raw `skills.category` values — free text, same reasoning as `locations`.
  categories: z.array(z.string()).optional(),
  skillTypes: z.array(z.enum(SKILL_TYPES)).optional(),
  // 'results' = the report has been run; otherwise the criteria screen shows.
  view: z.enum(['filters', 'results']).optional(),
});
type SkillMatrixSearch = z.infer<typeof search>;

export const Route = createFileRoute('/_authenticated/reports/skill-matrix')({
  validateSearch: search,
  component: SkillMatrixPage,
});

/**
 * The window the Utilization half of the grid reads hours over. Fixed to the
 * current month — there is no period control on this page, the same way the
 * skillMatrix model itself carries none (see its `dateColumn: null`). Never
 * actually null at runtime (only a `custom` selection can resolve to that),
 * but `resolveDateRange` has one signature for every preset.
 *
 * Called PER RENDER, not memoised at module scope: this is a long-lived SPA
 * page, and a month boundary crossed while the tab stays open must move the
 * window forward rather than keep querying/exporting last month's data under
 * a "this month" label. Every other `resolveDateRange({ preset:
 * 'current_month' })` call site in the app (`new-dashboard.ts`'s
 * `emptyDashboard()`, among others) is inside a function for the same reason.
 */
function currentMonth(): { from: string; to: string } {
  const resolved = resolveDateRange({ preset: 'current_month' });
  return { from: resolved?.from ?? '', to: resolved?.to ?? '' };
}

/** An "is any of" rule — every criterion on this page is one. */
function inFilter(field: string, values: readonly string[]): FilterRule {
  return {
    id: field,
    field,
    op: 'in',
    value: { kind: 'list', values: [...values] },
    paused: false,
  };
}

/** "3 skills", or `null` for an unset criterion — left out of the summary entirely. */
function plural(count: number, singular: string, irregular?: string): string | null {
  if (count === 0) return null;
  return `${count} ${count === 1 ? singular : (irregular ?? `${singular}s`)}`;
}

// "N - Label": the Skill scale's wording (`PROFICIENCY_LEVEL_LABELS` in
// `proficiency-scale-reference.tsx`), not the Application one — the two
// name level 2 differently ("Basic" vs "Basic User") and this one threshold
// applies across both at once, so it has to pick one wording to show.
//
// Still compiles to `gte` (a minimum, not an exact pick) — see the `filters`
// built below — the label is just shorter than spelling that out.
const MIN_PROFICIENCY_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({
  value: String(n),
  label: `${n} - ${PROFICIENCY_LEVEL_LABELS.skill[n]}`,
}));

/** "Any", or the matching option's own label — never the bare value. */
function minProficiencyLabel(level: string | undefined): string {
  if (!level) return 'Any';
  return MIN_PROFICIENCY_OPTIONS.find((o) => o.value === level)?.label ?? 'Any';
}

// Matches the dimension's own authored labels (`core/analytics/model.ts`'s
// `ratingStatus`/`skillType` `capability.options`) so this filter reads
// exactly like the same dimension would in the generic Report Builder.
const RATING_STATUS_LABELS: Record<RatingStatus, string> = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
};
const RATING_STATUS_OPTIONS = RATING_STATUSES.map((s) => ({
  value: s,
  label: RATING_STATUS_LABELS[s],
}));

const SKILL_TYPE_LABELS: Record<SkillType, string> = { skill: 'Skill', application: 'Application' };
const SKILL_TYPE_OPTIONS = SKILL_TYPES.map((t) => ({ value: t, label: SKILL_TYPE_LABELS[t] }));

/**
 * Two checkboxes over a closed set, not a multi-select: `current.length ===
 * 0` already means "both" (no filter — see `skillsFilters` below), so a
 * checkbox's checked state reads straight off that without first having to
 * materialize the pair. Toggling WRITES the explicit pair first, then
 * collapses back to `[]` the moment both are checked OR both are
 * unchecked — the two states look opposite but neither has an honest filter
 * to apply (nothing refuses `skillType IN ()`, but it isn't a real
 * question, and "both" already means "don't ask").
 */
function toggleSkillType(current: readonly string[], type: SkillType, checked: boolean): string[] {
  const effective = current.length === 0 ? [...SKILL_TYPES] : current;
  const next = checked
    ? Array.from(new Set([...effective, type]))
    : effective.filter((t) => t !== type);
  return next.length === 0 || next.length === SKILL_TYPES.length ? [] : next;
}

function SkillMatrixPage() {
  const { permissions, loading } = useMe();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const employeeIds = s.employeeIds ?? [];
  const skillIds = s.skillIds ?? [];
  const locations = s.locations ?? [];
  const minProficiencyLevel = s.minProficiencyLevel;
  const ratingStatuses = s.ratingStatuses ?? [];
  const categories = s.categories ?? [];
  const skillTypes = s.skillTypes ?? [];
  const update = (next: Partial<SkillMatrixSearch>) =>
    navigate({ search: (prev: SkillMatrixSearch) => ({ ...prev, ...next }), replace: true });

  // Undecided until `/me` answers — drawing either screen early would flash
  // the wrong one and then swap it out.
  if (loading) {
    return (
      <PageShell>
        <PageHeader title="Skill matrix" />
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }
  if (!permissions.has('skills.view')) {
    return <NoAccess permission="skills.view" resource="the skill matrix" />;
  }

  if (s.view !== 'results') {
    return (
      <FilterScreen
        employeeIds={employeeIds}
        skillIds={skillIds}
        locations={locations}
        minProficiencyLevel={minProficiencyLevel}
        ratingStatuses={ratingStatuses}
        categories={categories}
        skillTypes={skillTypes}
        onEmployees={(ids) => update({ employeeIds: ids.length > 0 ? ids : undefined })}
        onSkills={(ids) => update({ skillIds: ids.length > 0 ? ids : undefined })}
        onLocations={(next) => update({ locations: next.length > 0 ? next : undefined })}
        onMinProficiencyLevel={(next) =>
          update({
            minProficiencyLevel: next as '1' | '2' | '3' | '4' | '5' | undefined,
          })
        }
        onRatingStatuses={(next) =>
          update({ ratingStatuses: next.length > 0 ? (next as RatingStatus[]) : undefined })
        }
        onCategories={(next) => update({ categories: next.length > 0 ? next : undefined })}
        onSkillTypes={(next) =>
          update({ skillTypes: next.length > 0 ? (next as SkillType[]) : undefined })
        }
        onRun={() => update({ view: 'results' })}
      />
    );
  }

  return (
    <ResultsScreen
      employeeIds={employeeIds}
      skillIds={skillIds}
      locations={locations}
      minProficiencyLevel={minProficiencyLevel}
      ratingStatuses={ratingStatuses}
      categories={categories}
      skillTypes={skillTypes}
      onEditFilters={() => update({ view: 'filters' })}
    />
  );
}

// ── Screen 1: pick criteria, then run ───────────────────────────────────────
function FilterScreen(props: {
  employeeIds: string[];
  skillIds: string[];
  locations: string[];
  minProficiencyLevel: '1' | '2' | '3' | '4' | '5' | undefined;
  ratingStatuses: string[];
  categories: string[];
  skillTypes: string[];
  onEmployees: (ids: string[]) => void;
  onSkills: (ids: string[]) => void;
  onLocations: (next: string[]) => void;
  onMinProficiencyLevel: (next: string | undefined) => void;
  onRatingStatuses: (next: string[]) => void;
  onCategories: (next: string[]) => void;
  onSkillTypes: (next: string[]) => void;
  onRun: () => void;
}) {
  const CURRENT_MONTH = currentMonth();

  // Raw distinct `users.location` values in scope — there is no Location
  // catalog, so this reads the same lookup the generic Report Builder's
  // filter bar uses for any dimension with no authored option list
  // (`capability.options` left unset on `location`), scoped by the SAME
  // visibility (`reportReach`) the report itself runs under. Asked of
  // `utilization` rather than `skillMatrix`: every active employee has a
  // capacity row, where `skillMatrix` only has one for someone already
  // rated on a skill — a location with nobody rated yet would otherwise be
  // missing from the very filter meant to help find them.
  const locationOptions = useQuery({
    queryKey: queryKeys.analytics.dimensionValues('utilization', 'location', {
      from: CURRENT_MONTH.from,
      to: CURRENT_MONTH.to,
      limit: 200,
    }),
    queryFn: ({ signal }) =>
      analyticsApi.dimensionValues(
        'utilization',
        'location',
        { from: CURRENT_MONTH.from, to: CURRENT_MONTH.to, limit: 200 },
        signal,
      ),
  });

  // Distinct `skills.category` values among skills rated by somebody in
  // scope — same lookup as Location, asked of `skillMatrix` instead since
  // Category is `attributeOf: ['skill']`, not `['employee']`, and Utilization
  // has no skill-side dimensions at all.
  const categoryOptions = useQuery({
    queryKey: queryKeys.analytics.dimensionValues('skillMatrix', 'skillCategory', {
      from: CURRENT_MONTH.from,
      to: CURRENT_MONTH.to,
      limit: 200,
    }),
    queryFn: ({ signal }) =>
      analyticsApi.dimensionValues(
        'skillMatrix',
        'skillCategory',
        { from: CURRENT_MONTH.from, to: CURRENT_MONTH.to, limit: 200 },
        signal,
      ),
  });

  return (
    <PageShell>
      <PageHeader
        title="Skill matrix"
        subtitle="Choose who and which skills, then run the report."
      />

      <div className="surface-card max-w-xl space-y-5 rounded-lg border p-5">
        <div className="grid gap-1.5">
          <Label htmlFor="skill-matrix-employees">Employees</Label>
          <EntityMultiSelect<User>
            id="skill-matrix-employees"
            className="w-full"
            {...userPickerConfig}
            selected={props.employeeIds}
            onChange={props.onEmployees}
            allLabel="All employees"
            searchPlaceholder="Search employees…"
            renderItem={(u, sel) => <UserRow user={u} isSelected={sel} />}
            popoverWidth="auto"
          />
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="skill-matrix-skills">Skills</Label>
          <EntityMultiSelect<Skill>
            id="skill-matrix-skills"
            className="w-full"
            {...skillPickerConfig}
            loadStrategy="preload"
            selected={props.skillIds}
            onChange={props.onSkills}
            allLabel="All skills"
            searchPlaceholder="Search skills…"
            popoverWidth="auto"
          />
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="skill-matrix-locations">Location</Label>
          <MultiSelect
            id="skill-matrix-locations"
            className="w-full"
            options={(locationOptions.data?.data ?? []).map((o) => ({
              value: o.key,
              label: o.label,
            }))}
            selected={props.locations}
            onChange={props.onLocations}
            allLabel="All locations"
          />
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="skill-matrix-category">Category</Label>
          {/* `Picker`, not `MultiSelect`: full-width with every selection
              shown as its own removable chip, same as the Employees/Skills
              pickers above — `MultiSelect`'s trigger only ever prints a
              count. `Picker` takes a plain static option list with no
              async layer of its own (`EntityPicker` is what adds that, for
              id-backed entities), which is exactly Category's shape here —
              a resolved list of distinct `skillCategory` values, not ids. */}
          <Picker
            multiple
            id="skill-matrix-category"
            className="w-full"
            title="Category"
            placeholder="All categories"
            loading={categoryOptions.isLoading}
            options={(categoryOptions.data?.data ?? []).map((o) => ({
              id: o.key,
              label: o.label,
            }))}
            value={props.categories}
            onChange={props.onCategories}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="skill-matrix-proficiency">Minimum proficiency</Label>
            <Select
              value={props.minProficiencyLevel ?? 'any'}
              onValueChange={(v) => props.onMinProficiencyLevel(v === 'any' ? undefined : v)}
            >
              <SelectTrigger id="skill-matrix-proficiency" className="w-full">
                {/* Explicit children, not just `placeholder`: Radix's
                    `Select.Value` otherwise reads the selected item's label
                    off whatever text `Select.Content` happened to have
                    mounted, which is empty/stale on first paint (nothing has
                    opened the popover yet) — printing the raw `value` until
                    it does. Computing the label here ourselves is always
                    right, mount order or not. */}
                <SelectValue placeholder="Any">
                  {minProficiencyLabel(props.minProficiencyLevel)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                {MIN_PROFICIENCY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="skill-matrix-rating-status">Rating status</Label>
            <MultiSelect
              id="skill-matrix-rating-status"
              className="w-full"
              options={RATING_STATUS_OPTIONS}
              selected={props.ratingStatuses}
              onChange={props.onRatingStatuses}
              allLabel="All (excl. rejected)"
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Skill type</Label>
            <div className="flex h-9 items-center gap-4">
              {SKILL_TYPE_OPTIONS.map((o) => {
                const id = `skill-matrix-skill-type-${o.value}`;
                const checked = props.skillTypes.length === 0 || props.skillTypes.includes(o.value);
                return (
                  <div key={o.value} className="flex items-center gap-2">
                    <Checkbox
                      id={id}
                      checked={checked}
                      onCheckedChange={(c) =>
                        props.onSkillTypes(toggleSkillType(props.skillTypes, o.value, c === true))
                      }
                    />
                    <Label htmlFor={id} className="font-normal">
                      {o.label}
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <Button onClick={props.onRun}>Run report</Button>
      </div>
    </PageShell>
  );
}

// ── Screen 2: results ───────────────────────────────────────────────────────
function ResultsScreen(props: {
  employeeIds: string[];
  skillIds: string[];
  locations: string[];
  minProficiencyLevel: '1' | '2' | '3' | '4' | '5' | undefined;
  ratingStatuses: string[];
  categories: string[];
  skillTypes: string[];
  onEditFilters: () => void;
}) {
  const {
    employeeIds,
    skillIds,
    locations,
    minProficiencyLevel,
    ratingStatuses,
    categories,
    skillTypes,
  } = props;
  const CURRENT_MONTH = currentMonth();

  // `.parse`, not a literal: `ReportSpec` fills in several fields (rowSort,
  // colSort, period…) by default, and the type callers get back — including
  // `queryKeys.analytics.query` and `analyticsApi.query` below — is the
  // DEFAULTED shape, not what somebody bothered to type out here.
  const skillsFilters: FilterRule[] = [
    // Excludes `rejected` by default — a rejected rating isn't a valid claim
    // about the employee's skill, so it shows as blank rather than as a
    // value nobody should rely on. An explicit Rating Status choice
    // overrides this, `rejected` included, since that is then a deliberate
    // ask rather than this page's own default.
    ratingStatuses.length > 0
      ? inFilter('ratingStatus', ratingStatuses)
      : inFilter('ratingStatus', ['draft', 'pending_review', 'approved']),
    ...(employeeIds.length > 0 ? [inFilter('employee', employeeIds)] : []),
    ...(skillIds.length > 0 ? [inFilter('skill', skillIds)] : []),
    ...(locations.length > 0 ? [inFilter('location', locations)] : []),
    // `proficiencyLevel` is a `number`-typed dimension — its compiler only
    // accepts the ordered operators, so this is `gte` (a threshold), not an
    // `in` rule like every other criterion here.
    ...(minProficiencyLevel
      ? [
          {
            id: 'proficiencyLevel',
            field: 'proficiencyLevel',
            op: 'gte' as const,
            value: { kind: 'number' as const, value: Number(minProficiencyLevel) },
            paused: false,
          },
        ]
      : []),
    ...(categories.length > 0 ? [inFilter('skillCategory', categories)] : []),
    ...(skillTypes.length > 0 ? [inFilter('skillType', skillTypes)] : []),
  ];
  const skillsSpec = reportSpecSchema.parse({
    model: 'skillMatrix',
    kind: 'detail',
    from: CURRENT_MONTH.from,
    to: CURRENT_MONTH.to,
    columns: ['employee', 'employeeEmail', 'skill', 'proficiencyLevel', 'skillType'],
    measures: [],
    filters: skillsFilters,
  });

  // No skill-side dimension on Utilization — Employee and Location are the
  // only two criteria that carry over.
  const utilizationFilters: FilterRule[] = [
    ...(employeeIds.length > 0 ? [inFilter('employee', employeeIds)] : []),
    ...(locations.length > 0 ? [inFilter('location', locations)] : []),
  ];
  const utilizationSpec = reportSpecSchema.parse({
    model: 'utilization',
    kind: 'pivot',
    from: CURRENT_MONTH.from,
    to: CURRENT_MONTH.to,
    // `location` rides along on the ROW axis purely to carry the value back —
    // it's 1:1 with `employee`, so this doesn't change the grain, just adds
    // it to each row's `dims` for the grid's Location row.
    rows: ['employee', 'location'],
    columns: [],
    measures: ['availableHours'],
    filters: utilizationFilters,
  });

  const skillsQuery = useQuery({
    queryKey: queryKeys.analytics.query(skillsSpec),
    queryFn: ({ signal }) => analyticsApi.query(skillsSpec, signal),
  });
  const utilizationQuery = useQuery({
    queryKey: queryKeys.analytics.query(utilizationSpec),
    queryFn: ({ signal }) => analyticsApi.query(utilizationSpec, signal),
  });

  const isLoading = skillsQuery.isLoading || utilizationQuery.isLoading;
  const error = skillsQuery.error ?? utilizationQuery.error;
  const grid =
    skillsQuery.data && utilizationQuery.data
      ? buildSkillMatrixGrid(skillsQuery.data.rows, utilizationQuery.data.rows)
      : null;

  // The SAME two specs already run above, sent back so the server performs
  // the identical join and the file matches what's on screen — see
  // `AnalyticsClient.exportSkillMatrix`.
  const exportMutation = useMutation({
    mutationFn: () =>
      analyticsApi.exportSkillMatrix(skillsSpec, utilizationSpec, { name: 'Skill matrix' }),
    onSuccess: ({ blob, filename }) => triggerDownload(blob, filename ?? 'skill-matrix.xlsx'),
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not export the skill matrix'),
  });

  // Only the criteria actually narrowed, so the common case ("just run it")
  // reads as one short sentence rather than a wall of "All X"s.
  const criteriaSummary =
    [
      plural(employeeIds.length, 'employee'),
      plural(skillIds.length, 'skill'),
      plural(locations.length, 'location'),
      minProficiencyLevel ? `Proficiency ${minProficiencyLabel(minProficiencyLevel)}` : null,
      plural(ratingStatuses.length, 'status'),
      plural(categories.length, 'category', 'categories'),
      plural(skillTypes.length, 'type'),
    ]
      .filter((s): s is string => s !== null)
      .join(' · ') || 'All employees and skills';

  return (
    <PageShell>
      <PageHeader title="Skill matrix" subtitle={criteriaSummary}>
        <Button variant="outline" onClick={props.onEditFilters}>
          <SlidersHorizontal />
          Edit filters
        </Button>
        <Button
          onClick={() => exportMutation.mutate()}
          disabled={exportMutation.isPending || isLoading || !!error}
        >
          <Download />
          {exportMutation.isPending ? 'Exporting…' : 'Export'}
        </Button>
      </PageHeader>

      {isLoading ? (
        <p className="py-10 text-center text-muted-foreground text-sm">Loading…</p>
      ) : error ? (
        <p className="py-10 text-center text-destructive text-sm">
          {error instanceof ApiError ? error.message : 'Could not load the skill matrix.'}
        </p>
      ) : grid ? (
        <SkillMatrixGridView grid={grid} />
      ) : null}
    </PageShell>
  );
}
